import type { ThemeConfig } from 'antd';
import { theme as AntdTheme } from 'antd';

export const aiThemePresets: { [key: string]: ThemeConfig } = {
  modernLight: {
    token: {
      colorPrimary: '#1890ff',
      borderRadius: 8,
      fontSize: 14,
      fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'`,
      colorSuccess: '#52c41a',
      colorWarning: '#faad14',
      colorError: '#ff4d4f',
      colorInfo: '#1890ff',
      colorLink: '#1890ff',
      colorLinkHover: '#69b1ff',
      colorLinkActive: '#0958d9',
      colorTextBase: '#333333',
      colorBgBase: '#f0f2f5',
    },
  },
  darkProfessional: {
    algorithm: AntdTheme.darkAlgorithm,
    token: {
      colorPrimary: '#1890ff',
      borderRadius: 4,
      fontSize: 14,
      fontFamily: `'Roboto Mono', monospace`,
      colorSuccess: '#52c41a',
      colorWarning: '#faad14',
      colorError: '#ff4d4f',
      colorInfo: '#1890ff',
      colorLink: '#1890ff',
      colorLinkHover: '#69b1ff',
      colorLinkActive: '#0958d9',
      colorTextBase: '#ffffff',
      colorBgBase: '#1f1f1f',
    },
  },
  elegantDark: {
    algorithm: AntdTheme.darkAlgorithm,
    token: {
      colorPrimary: '#2a4d69',
      borderRadius: 6,
      fontSize: 14,
      fontFamily: `'Georgia', serif`,
      colorSuccess: '#73d13d',
      colorWarning: '#ffc53d',
      colorError: '#ff4d4f',
      colorInfo: '#2a4d69',
      colorLink: '#ffd700',
      colorLinkHover: '#ffea70',
      colorLinkActive: '#b39700',
      colorTextBase: '#e0e0e0',
      colorBgBase: '#141414',
    },
  },
  vibrantLight: {
    token: {
      colorPrimary: '#eb2f96',
      borderRadius: 12,
      fontSize: 15,
      fontFamily: `'Poppins', sans-serif`,
      colorSuccess: '#52c41a',
      colorWarning: '#faad14',
      colorError: '#f5222d',
      colorInfo: '#eb2f96',
      colorLink: '#eb2f96',
      colorLinkHover: '#f759ac',
      colorLinkActive: '#c41d7f',
      colorTextBase: '#262626',
      colorBgBase: '#ffffff',
    },
  },
  minimalist: {
    token: {
      colorPrimary: '#595959',
      borderRadius: 2,
      fontSize: 14,
      fontFamily: `'Inter', sans-serif`,
      colorSuccess: '#8c8c8c',
      colorWarning: '#bfbfbf',
      colorError: '#d9d9d9',
      colorInfo: '#595959',
      colorLink: '#595959',
      colorLinkHover: '#8c8c8c',
      colorLinkActive: '#262626',
      colorTextBase: '#000000',
      colorBgBase: '#fafafa',
    },
  },
  corporateBlue: {
    token: {
      colorPrimary: '#003a8c',
      borderRadius: 4,
      fontSize: 14,
      fontFamily: `'Lato', sans-serif`,
      colorSuccess: '#52c41a',
      colorWarning: '#faad14',
      colorError: '#ff4d4f',
      colorInfo: '#0050b3',
      colorLink: '#0050b3',
      colorLinkHover: '#40a9ff',
      colorLinkActive: '#003a8c',
      colorTextBase: '#333333',
      colorBgBase: '#f5f7fa',
    },
  },
  // New presets
  oceanic: {
    token: {
      colorPrimary: '#1a5276',
      borderRadius: 6,
      fontSize: 14,
      fontFamily: `'Open Sans', sans-serif`,
      colorSuccess: '#28a745',
      colorWarning: '#ffc107',
      colorError: '#dc3545',
      colorInfo: '#17a2b8',
      colorLink: '#1a5276',
      colorLinkHover: '#217dbb',
      colorLinkActive: '#103a52',
      colorTextBase: '#343a40',
      colorBgBase: '#e9ecef',
    },
  },
  forestGreen: {
    token: {
      colorPrimary: '#28a745',
      borderRadius: 8,
      fontSize: 14,
      fontFamily: `'Montserrat', sans-serif`,
      colorSuccess: '#20c997',
      colorWarning: '#ffc107',
      colorError: '#dc3545',
      colorInfo: '#17a2b8',
      colorLink: '#28a745',
      colorLinkHover: '#218838',
      colorLinkActive: '#1e7e34',
      colorTextBase: '#343a40',
      colorBgBase: '#f8f9fa',
    },
  },
  sunsetOrange: {
    token: {
      colorPrimary: '#fd7e14',
      borderRadius: 10,
      fontSize: 16,
      fontFamily: `'Lato', sans-serif`,
      colorSuccess: '#28a745',
      colorWarning: '#ffc107',
      colorError: '#dc3545',
      colorInfo: '#17a2b8',
      colorLink: '#fd7e14',
      colorLinkHover: '#e85d04',
      colorLinkActive: '#c24100',
      colorTextBase: '#212529',
      colorBgBase: '#fff3cd',
    },
  },
  // Additional presets
  calmBlue: {
    token: {
      colorPrimary: '#4a90e2',
      borderRadius: 8,
      fontSize: 14,
      fontFamily: `'Open Sans', sans-serif`,
      colorSuccess: '#5cb85c',
      colorWarning: '#f0ad4e',
      colorError: '#d9534f',
      colorInfo: '#5bc0de',
      colorLink: '#4a90e2',
      colorLinkHover: '#6aa3ed',
      colorLinkActive: '#3a7bd5',
      colorTextBase: '#333333',
      colorBgBase: '#f5f8fa',
    },
  },
  warmGray: {
    token: {
      colorPrimary: '#6c757d',
      borderRadius: 4,
      fontSize: 14,
      fontFamily: `'Roboto', sans-serif`,
      colorSuccess: '#28a745',
      colorWarning: '#ffc107',
      colorError: '#dc3545',
      colorInfo: '#17a2b8',
      colorLink: '#6c757d',
      colorLinkHover: '#868e96',
      colorLinkActive: '#5a6268',
      colorTextBase: '#343a40',
      colorBgBase: '#f8f9fa',
    },
  },
  deepPurple: {
    algorithm: AntdTheme.darkAlgorithm,
    token: {
      colorPrimary: '#6f42c1',
      borderRadius: 10,
      fontSize: 15,
      fontFamily: `'Montserrat', sans-serif`,
      colorSuccess: '#20c997',
      colorWarning: '#ffc107',
      colorError: '#dc3545',
      colorInfo: '#17a2b8',
      colorLink: '#6f42c1',
      colorLinkHover: '#8b6cd1',
      colorLinkActive: '#5a35a0',
      colorTextBase: '#e0e0e0',
      colorBgBase: '#211a2e',
    },
  },
  // Industry-specific presets
  healthcare: {
    token: {
      colorPrimary: '#007bff', // Blue for trust and professionalism
      borderRadius: 6,
      fontSize: 14,
      fontFamily: `'Open Sans', sans-serif`,
      colorSuccess: '#28a745',
      colorWarning: '#ffc107',
      colorError: '#dc3545',
      colorInfo: '#17a2b8',
      colorLink: '#007bff',
      colorLinkHover: '#0056b3',
      colorLinkActive: '#004085',
      colorTextBase: '#343a40',
      colorBgBase: '#f8f9fa',
    },
  },
  finance: {
    token: {
      colorPrimary: '#003366', // Dark blue for stability and security
      borderRadius: 4,
      fontSize: 14,
      fontFamily: `'Roboto', sans-serif`,
      colorSuccess: '#28a745',
      colorWarning: '#ffc107',
      colorError: '#dc3545',
      colorInfo: '#17a2b8',
      colorLink: '#003366',
      colorLinkHover: '#0056b3',
      colorLinkActive: '#002244',
      colorTextBase: '#212529',
      colorBgBase: '#f5f5f5',
    },
  },
  technology: {
    algorithm: AntdTheme.darkAlgorithm,
    token: {
      colorPrimary: '#00c4a7', // Teal for innovation and modernity
      borderRadius: 8,
      fontSize: 15,
      fontFamily: `'Space Mono', monospace`,
      colorSuccess: '#20c997',
      colorWarning: '#ffc107',
      colorError: '#dc3545',
      colorInfo: '#17a2b8',
      colorLink: '#00c4a7',
      colorLinkHover: '#00e0c0',
      colorLinkActive: '#009980',
      colorTextBase: '#e0e0e0',
      colorBgBase: '#1a1a2e',
    },
  },
  education: {
    token: {
      colorPrimary: '#8a2be2', // Blue-violet for creativity and wisdom
      borderRadius: 8,
      fontSize: 14,
      fontFamily: `'Montserrat', sans-serif`,
      colorSuccess: '#28a745',
      colorWarning: '#ffc107',
      colorError: '#dc3545',
      colorInfo: '#17a2b8',
      colorLink: '#8a2be2',
      colorLinkHover: '#9932cc',
      colorLinkActive: '#6a1a9e',
      colorTextBase: '#343a40',
      colorBgBase: '#fdfefe',
    },
  },
  retail: {
    token: {
      colorPrimary: '#ff4d4f', // Red for energy and excitement
      borderRadius: 10,
      fontSize: 14,
      fontFamily: `'Poppins', sans-serif`,
      colorSuccess: '#52c41a',
      colorWarning: '#faad14',
      colorError: '#ff4d4f',
      colorInfo: '#1890ff',
      colorLink: '#ff4d4f',
      colorLinkHover: '#ff7875',
      colorLinkActive: '#d9363e',
      colorTextBase: '#262626',
      colorBgBase: '#fff0f0',
    },
  },
  // New Google-inspired preset with more comprehensive UI changes
  googleInspired: {
    token: {
      colorPrimary: '#4285F4', // Google Blue
      borderRadius: 8,
      fontSize: 14,
      fontFamily: `'Roboto', sans-serif`,
      colorSuccess: '#34A853', // Google Green
      colorWarning: '#FBBC05', // Google Yellow
      colorError: '#EA4335', // Google Red
      colorInfo: '#4285F4',
      colorLink: '#1A73E8',
      colorLinkHover: '#174EA6',
      colorLinkActive: '#0B3E8F',
      colorTextBase: '#202124',
      colorBgBase: '#F8F9FA',
    },
    components: {
      Button: {
        colorPrimary: '#4285F4',
      //  colorText: '#FFFFFF',
        controlHeight: 40,
        borderRadius: 4,
        // paddingInline: 24, // Removed invalid property
        fontSize: 15,
        fontWeight: 500,
        boxShadow: '0 2px 2px 0 rgba(0,0,0,0.14), 0 3px 1px -2px rgba(0,0,0,0.12), 0 1px 5px 0 rgba(0,0,0,0.20)',
        algorithm: true, // Apply algorithm to button for consistent styling
      },
      Input: {
        controlHeight: 40,
        borderRadius: 4,
        // paddingInline: 12, // Removed invalid property
        fontSize: 15,
        colorBorder: '#DADCE0',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#4285F4',
        hoverBorderColor: '#4285F4',
      },
      Card: {
        headerBg: '#F8F9FA',
        headerHeight: 56,
        paddingLG: 24,
        borderRadius: 8,
        boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.06)',
      },
      Table: {
        headerBg: '#F8F9FA',
        headerColor: '#5F6368',
        rowHoverBg: '#F0F0F0',
        borderColor: '#E0E0E0',
        fontSize: 14,
        // padding: 16, // Removed invalid property
      },
      Select: {
        controlHeight: 40,
        borderRadius: 4,
        // paddingInline: 12, // Removed invalid property
        fontSize: 15,
        colorBorder: '#DADCE0',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#4285F4',
        hoverBorderColor: '#4285F4',
      },
      DatePicker: {
        controlHeight: 40,
        borderRadius: 4,
        // paddingInline: 12, // Removed invalid property
        fontSize: 15,
        colorBorder: '#DADCE0',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#4285F4',
        hoverBorderColor: '#4285F4',
      },
      Switch: {
        colorPrimary: '#4285F4',
        colorPrimaryHover: '#1A73E8',
        colorTextTertiary: '#DADCE0',
      },
    },
  },
  // UI Frame Style Presets
  tailwindUi: {
    token: {
      colorPrimary: '#06B6D4', // Tailwind CSS cyan-500
      borderRadius: 0.25 * 16, // 0.25rem * 16px/rem = 4px (rounded-sm)
      fontSize: 16,
      fontFamily: `'Inter', sans-serif`,
      colorSuccess: '#22C55E', // Tailwind CSS green-500
      colorWarning: '#F59E0B', // Tailwind CSS amber-500
      colorError: '#EF4444', // Tailwind CSS red-500
      colorInfo: '#0EA5E9', // Tailwind CSS sky-500
      colorLink: '#06B6D4',
      colorLinkHover: '#0891B2', // Tailwind CSS cyan-600
      colorLinkActive: '#0E7490', // Tailwind CSS cyan-700
      colorTextBase: '#1F2937', // Tailwind CSS gray-800
      colorBgBase: '#F9FAFB', // Tailwind CSS gray-50
    },
    components: {
      Button: {
        colorPrimary: '#06B6D4',
       // colorText: '#FFFFFF',
        controlHeight: 40,
        borderRadius: 4,
        fontSize: 16,
        fontWeight: 600,
        boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
        algorithm: true,
      },
      Input: {
        controlHeight: 40,
        borderRadius: 4,
        fontSize: 16,
        colorBorder: '#D1D5DB', // Tailwind CSS gray-300
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#06B6D4',
        hoverBorderColor: '#06B6D4',
      },
      Card: {
        headerBg: '#FFFFFF',
        headerHeight: 56,
        paddingLG: 24,
        borderRadius: 8,
        boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.06)',
      },
      Table: {
        headerBg: '#F9FAFB',
        headerColor: '#4B5563', // Tailwind CSS gray-600
        rowHoverBg: '#F3F4F6', // Tailwind CSS gray-100
        borderColor: '#E5E7EB', // Tailwind CSS gray-200
        fontSize: 14,
      },
      Select: {
        controlHeight: 40,
        borderRadius: 4,
        fontSize: 16,
        colorBorder: '#D1D5DB',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#06B6D4',
        hoverBorderColor: '#06B6D4',
      },
      DatePicker: {
        controlHeight: 40,
        borderRadius: 4,
        fontSize: 16,
        colorBorder: '#D1D5DB',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#06B6D4',
        hoverBorderColor: '#06B6D4',
      },
      Switch: {
        colorPrimary: '#06B6D4',
        colorPrimaryHover: '#0891B2',
        colorTextTertiary: '#D1D5DB',
      },
    },
  },
  shadcnUi: {
    token: {
      colorPrimary: '#020817', // Shadcn UI primary (slate-950)
      borderRadius: 0.5 * 16, // 0.5rem * 16px/rem = 8px
      fontSize: 16,
      fontFamily: `'Inter', sans-serif`,
      colorSuccess: '#22C55E', // Green (example, adjust as needed)
      colorWarning: '#F59E0B', // Amber (example, adjust as needed)
      colorError: '#EF4444', // Red (example, adjust as needed)
      colorInfo: '#3B82F6', // Blue (example, adjust as needed)
      colorLink: '#020817',
      colorLinkHover: '#1E293B', // Shadcn UI primary-foreground (slate-800)
      colorLinkActive: '#0F172A', // Shadcn UI primary-foreground (slate-900)
      colorTextBase: '#020817', // Shadcn UI foreground (slate-950)
      colorBgBase: '#FFFFFF', // Shadcn UI background
    },
    components: {
      Button: {
        colorPrimary: '#020817',
       // colorText: '#FFFFFF',
        controlHeight: 40,
        borderRadius: 8,
        fontSize: 16,
        fontWeight: 500,
        boxShadow: 'none',
        algorithm: true,
      },
      Input: {
        controlHeight: 40,
        borderRadius: 8,
        fontSize: 16,
        colorBorder: '#E2E8F0', // Shadcn UI input (slate-200)
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#020817',
        hoverBorderColor: '#020817',
      },
      Card: {
        headerBg: '#FFFFFF',
        headerHeight: 56,
        paddingLG: 24,
        borderRadius: 8,
        boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.06)',
      },
      Table: {
        headerBg: '#F8FAFC', // Shadcn UI muted (slate-50)
        headerColor: '#64748B', // Shadcn UI muted-foreground (slate-500)
        rowHoverBg: '#F1F5F9', // Shadcn UI accent (slate-100)
        borderColor: '#E2E8F0', // Shadcn UI border (slate-200)
        fontSize: 14,
      },
      Select: {
        controlHeight: 40,
        borderRadius: 8,
        fontSize: 16,
        colorBorder: '#E2E8F0',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#020817',
        hoverBorderColor: '#020817',
      },
      DatePicker: {
        controlHeight: 40,
        borderRadius: 8,
        fontSize: 16,
        colorBorder: '#E2E8F0',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#020817',
        hoverBorderColor: '#020817',
      },
      Switch: {
        colorPrimary: '#020817',
        colorPrimaryHover: '#1E293B',
        colorTextTertiary: '#E2E8F0',
      },
    },
  },
  tailwind: {
    token: {
      colorPrimary: '#4F46E5', // Indigo-600
      borderRadius: 0.375 * 16, // 0.375rem * 16px/rem = 6px (rounded-md)
      fontSize: 16,
      fontFamily: `'Inter', sans-serif`,
      colorSuccess: '#10B981', // Green-500
      colorWarning: '#FBBF24', // Amber-400
      colorError: '#EF4444', // Red-500
      colorInfo: '#3B82F6', // Blue-500
      colorLink: '#4F46E5',
      colorLinkHover: '#4338CA', // Indigo-700
      colorLinkActive: '#3730A3', // Indigo-800
      colorTextBase: '#1F2937', // Gray-800
      colorBgBase: '#F9FAFB', // Gray-50
    },
    components: {
      Button: {
        colorPrimary: '#4F46E5',
       // colorText: '#FFFFFF',
        controlHeight: 40,
        borderRadius: 6,
        fontSize: 16,
        fontWeight: 500,
        boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
        algorithm: true,
      },
      Input: {
        controlHeight: 40,
        borderRadius: 6,
        fontSize: 16,
        colorBorder: '#D1D5DB', // Gray-300
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#4F46E5',
        hoverBorderColor: '#4F46E5',
      },
      Card: {
        headerBg: '#FFFFFF',
        headerHeight: 56,
        paddingLG: 24,
        borderRadius: 8,
        boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.06)',
      },
      Table: {
        headerBg: '#F9FAFB',
        headerColor: '#4B5563', // Gray-600
        rowHoverBg: '#F3F4F6', // Gray-100
        borderColor: '#E5E7EB', // Gray-200
        fontSize: 14,
      },
      Select: {
        controlHeight: 40,
        borderRadius: 6,
        fontSize: 16,
        colorBorder: '#D1D5DB',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#4F46E5',
        hoverBorderColor: '#4F46E5',
      },
      DatePicker: {
        controlHeight: 40,
        borderRadius: 6,
        fontSize: 16,
        colorBorder: '#D1D5DB',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#4F46E5',
        hoverBorderColor: '#4F46E5',
      },
      Switch: {
        colorPrimary: '#4F46E5',
        colorPrimaryHover: '#4338CA',
        colorTextTertiary: '#D1D5DB',
      },
    },
  },
  heroui: {
    token: {
      colorPrimary: '#6366F1', // Indigo-500
      borderRadius: 0.5 * 16, // 0.5rem * 16px/rem = 8px
      fontSize: 16,
      fontFamily: `'Inter', sans-serif`,
      colorSuccess: '#10B981', // Green-500
      colorWarning: '#FBBF24', // Amber-400
      colorError: '#EF4444', // Red-500
      colorInfo: '#3B82F6', // Blue-500
      colorLink: '#6366F1',
      colorLinkHover: '#4F46E5', // Indigo-600
      colorLinkActive: '#4338CA', // Indigo-700
      colorTextBase: '#1F2937', // Gray-800
      colorBgBase: '#FFFFFF', // White
    },
    components: {
      Button: {
        colorPrimary: '#6366F1',
       // colorText: '#FFFFFF',
        controlHeight: 48, // Larger buttons
        borderRadius: 8,
        fontSize: 18,
        fontWeight: 600,
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
        algorithm: true,
      },
      Input: {
        controlHeight: 48,
        borderRadius: 8,
        fontSize: 16,
        colorBorder: '#D1D5DB', // Gray-300
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#6366F1',
        hoverBorderColor: '#6366F1',
      },
      Card: {
        headerBg: '#FFFFFF',
        headerHeight: 64, // Larger card headers
        paddingLG: 32, // More padding
        borderRadius: 12, // More rounded
        boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
      },
      Table: {
        headerBg: '#F9FAFB',
        headerColor: '#4B5563', // Gray-600
        rowHoverBg: '#F3F4F6', // Gray-100
        borderColor: '#E5E7EB', // Gray-200
        fontSize: 16, // Larger table font
      },
      Select: {
        controlHeight: 48,
        borderRadius: 8,
        fontSize: 16,
        colorBorder: '#D1D5DB',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#6366F1',
        hoverBorderColor: '#6366F1',
      },
      DatePicker: {
        controlHeight: 48,
        borderRadius: 8,
        fontSize: 16,
        colorBorder: '#D1D5DB',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#6366F1',
        hoverBorderColor: '#6366F1',
      },
      Switch: {
        colorPrimary: '#6366F1',
        colorPrimaryHover: '#4F46E5',
        colorTextTertiary: '#D1D5DB',
      },
    },
  },
  materialUi: {
    token: {
      colorPrimary: '#1976D2', // Material UI primary blue
      borderRadius: 4,
      fontSize: 16,
      fontFamily: `'Roboto', sans-serif`,
      colorSuccess: '#388E3C', // Material UI success green
      colorWarning: '#FBC02D', // Material UI warning yellow
      colorError: '#D32F2F', // Material UI error red
      colorInfo: '#0288D1', // Material UI info blue
      colorLink: '#1976D2',
      colorLinkHover: '#1565C0',
      colorLinkActive: '#0D47A1',
      colorTextBase: '#212121', // Material UI primary text
      colorBgBase: '#FFFFFF',
    },
    components: {
      Button: {
        colorPrimary: '#1976D2',
       // colorText: '#FFFFFF',
        controlHeight: 36,
        borderRadius: 4,
        fontSize: 14,
        fontWeight: 500,
        boxShadow: '0 3px 1px -2px rgba(0,0,0,0.2), 0 2px 2px 0 rgba(0,0,0,0.14), 0 1px 5px 0 rgba(0,0,0,0.12)',
        algorithm: true,
      },
      Input: {
        controlHeight: 56,
        borderRadius: 4,
        fontSize: 16,
        colorBorder: 'rgba(0, 0, 0, 0.23)',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#1976D2',
        hoverBorderColor: '#1976D2',
      },
      Card: {
        headerBg: '#FFFFFF',
        headerHeight: 56,
        paddingLG: 16,
        borderRadius: 4,
        boxShadow: '0 2px 1px -1px rgba(0,0,0,0.2), 0 1px 1px 0 rgba(0,0,0,0.14), 0 1px 3px 0 rgba(0,0,0,0.12)',
      },
      Table: {
        headerBg: '#F5F5F5',
        headerColor: 'rgba(0, 0, 0, 0.54)',
        rowHoverBg: 'rgba(0, 0, 0, 0.04)',
        borderColor: 'rgba(224, 224, 224, 1)',
        fontSize: 14,
      },
      Select: {
        controlHeight: 56,
        borderRadius: 4,
        fontSize: 16,
        colorBorder: 'rgba(0, 0, 0, 0.23)',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#1976D2',
        hoverBorderColor: '#1976D2',
      },
      DatePicker: {
        controlHeight: 56,
        borderRadius: 4,
        fontSize: 16,
        colorBorder: 'rgba(0, 0, 0, 0.23)',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#1976D2',
        hoverBorderColor: '#1976D2',
      },
      Switch: {
        colorPrimary: '#1976D2',
        colorPrimaryHover: '#1565C0',
        colorTextTertiary: 'rgba(0, 0, 0, 0.38)',
      },
    },
  },
  bootstrap: {
    token: {
      colorPrimary: '#0D6EFD', // Bootstrap primary blue
      borderRadius: 0.25 * 16, // 0.25rem
      fontSize: 16,
      fontFamily: `system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"`,
      colorSuccess: '#198754', // Bootstrap success green
      colorWarning: '#FFC107', // Bootstrap warning yellow
      colorError: '#DC3545', // Bootstrap danger red
      colorInfo: '#0DCAF0', // Bootstrap info cyan
      colorLink: '#0D6EFD',
      colorLinkHover: '#0A58CA',
      colorLinkActive: '#084298',
      colorTextBase: '#212529', // Bootstrap default text
      colorBgBase: '#FFFFFF',
    },
    components: {
      Button: {
        colorPrimary: '#0D6EFD',
       // colorText: '#FFFFFF',
        controlHeight: 38,
        borderRadius: 4,
        fontSize: 16,
        fontWeight: 400,
        boxShadow: 'none',
        algorithm: true,
      },
      Input: {
        controlHeight: 38,
        borderRadius: 4,
        fontSize: 16,
        colorBorder: '#CED4DA',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#86B7FE',
        hoverBorderColor: '#86B7FE',
      },
      Card: {
        headerBg: 'rgba(0, 0, 0, 0.03)',
        headerHeight: 50,
        paddingLG: 20,
        borderRadius: 4,
        boxShadow: 'none',
      },
      Table: {
        headerBg: '#F8F9FA',
        headerColor: '#212529',
        rowHoverBg: '#F8F9FA',
        borderColor: '#DEE2E6',
        fontSize: 16,
      },
      Select: {
        controlHeight: 38,
        borderRadius: 4,
        fontSize: 16,
        colorBorder: '#CED4DA',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#86B7FE',
        hoverBorderColor: '#86B7FE',
      },
      DatePicker: {
        controlHeight: 38,
        borderRadius: 4,
        fontSize: 16,
        colorBorder: '#CED4DA',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#86B7FE',
        hoverBorderColor: '#86B7FE',
      },
      Switch: {
        colorPrimary: '#0D6EFD',
        colorPrimaryHover: '#0A58CA',
        colorTextTertiary: '#CED4DA',
      },
    },
  },
  chakraUi: {
    token: {
      colorPrimary: '#3182CE', // Chakra UI blue.500
      borderRadius: 6,
      fontSize: 16,
      fontFamily: `'Inter', sans-serif`,
      colorSuccess: '#38A169', // Chakra UI green.500
      colorWarning: '#D69E2E', // Chakra UI yellow.500
      colorError: '#E53E3E', // Chakra UI red.500
      colorInfo: '#3182CE',
      colorLink: '#3182CE',
      colorLinkHover: '#2B6CB0', // Chakra UI blue.600
      colorLinkActive: '#2C5282', // Chakra UI blue.700
      colorTextBase: '#1A202C', // Chakra UI gray.800
      colorBgBase: '#FFFFFF',
    },
    components: {
      Button: {
        colorPrimary: '#3182CE',
        //colorText: '#FFFFFF',
        controlHeight: 40,
        borderRadius: 6,
        fontSize: 16,
        fontWeight: 600,
        boxShadow: 'none',
        algorithm: true,
      },
      Input: {
        controlHeight: 40,
        borderRadius: 6,
        fontSize: 16,
        colorBorder: '#E2E8F0', // Chakra UI gray.200
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#3182CE',
        hoverBorderColor: '#3182CE',
      },
      Card: {
        headerBg: '#FFFFFF',
        headerHeight: 56,
        paddingLG: 24,
        borderRadius: 6,
        boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.06)',
      },
      Table: {
        headerBg: '#F7FAFC', // Chakra UI gray.50
        headerColor: '#4A5568', // Chakra UI gray.600
        rowHoverBg: '#EDF2F7', // Chakra UI gray.100
        borderColor: '#E2E8F0', // Chakra UI gray.200
        fontSize: 14,
      },
      Select: {
        controlHeight: 40,
        borderRadius: 6,
        fontSize: 16,
        colorBorder: '#E2E8F0',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#3182CE',
        hoverBorderColor: '#3182CE',
      },
      DatePicker: {
        controlHeight: 40,
        borderRadius: 6,
        fontSize: 16,
        colorBorder: '#E2E8F0',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#3182CE',
        hoverBorderColor: '#3182CE',
      },
      Switch: {
        colorPrimary: '#3182CE',
        colorPrimaryHover: '#2B6CB0',
        colorTextTertiary: '#E2E8F0',
      },
    },
  },
  semanticUi: {
    token: {
      colorPrimary: '#2185D0', // Semantic UI primary blue
      borderRadius: 4,
      fontSize: 14,
      fontFamily: `Lato, 'Helvetica Neue', Arial, Helvetica, sans-serif`,
      colorSuccess: '#21BA45', // Semantic UI success green
      colorWarning: '#F2C037', // Semantic UI warning yellow
      colorError: '#DB2828', // Semantic UI error red
      colorInfo: '#2185D0',
      colorLink: '#2185D0',
      colorLinkHover: '#1678C2',
      colorLinkActive: '#0E6EB8',
      colorTextBase: 'rgba(0, 0, 0, 0.87)',
      colorBgBase: '#FFFFFF',
    },
    components: {
      Button: {
        colorPrimary: '#2185D0',
       // colorText: '#FFFFFF',
        controlHeight: 36,
        borderRadius: 4,
        fontSize: 14,
        fontWeight: 700,
        boxShadow: '0 0 0 1px transparent inset, 0 0 0 0 rgba(34,36,38,.15) inset',
        algorithm: true,
      },
      Input: {
        controlHeight: 38,
        borderRadius: 4,
        fontSize: 14,
        colorBorder: 'rgba(34,36,38,.15)',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#85B7D9',
        hoverBorderColor: '#85B7D9',
      },
      Card: {
        headerBg: '#FFFFFF',
        headerHeight: 48,
        paddingLG: 16,
        borderRadius: 4,
        boxShadow: '0 1px 3px 0 #D4D4D5, 0 0 0 1px #D4D4D5',
      },
      Table: {
        headerBg: '#F9FAFB',
        headerColor: 'rgba(0,0,0,.87)',
        rowHoverBg: 'rgba(0,0,0,.05)',
        borderColor: 'rgba(34,36,38,.15)',
        fontSize: 14,
      },
      Select: {
        controlHeight: 38,
        borderRadius: 4,
        fontSize: 14,
        colorBorder: 'rgba(34,36,38,.15)',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#85B7D9',
        hoverBorderColor: '#85B7D9',
      },
      DatePicker: {
        controlHeight: 38,
        borderRadius: 4,
        fontSize: 14,
        colorBorder: 'rgba(34,36,38,.15)',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#85B7D9',
        hoverBorderColor: '#85B7D9',
      },
      Switch: {
        colorPrimary: '#2185D0',
        colorPrimaryHover: '#1678C2',
        colorTextTertiary: 'rgba(34,36,38,.15)',
      },
    },
  },
  foundation: {
    token: {
      colorPrimary: '#1779BA', // Foundation primary blue
      borderRadius: 0,
      fontSize: 16,
      fontFamily: `'Helvetica Neue', Helvetica, Roboto, Arial, sans-serif`,
      colorSuccess: '#3ADB76', // Foundation success green
      colorWarning: '#FFAE00', // Foundation warning yellow
      colorError: '#CC4B37', // Foundation alert red
      colorInfo: '#1779BA',
      colorLink: '#1779BA',
      colorLinkHover: '#1468A0',
      colorLinkActive: '#115786',
      colorTextBase: '#0A0A0A', // Foundation default text
      colorBgBase: '#F2F2F2',
    },
    components: {
      Button: {
        colorPrimary: '#1779BA',
       // colorText: '#FFFFFF',
        controlHeight: 39,
        borderRadius: 0,
        fontSize: 16,
        fontWeight: 700,
        boxShadow: 'none',
        algorithm: true,
      },
      Input: {
        controlHeight: 39,
        borderRadius: 0,
        fontSize: 16,
        colorBorder: '#CACACA',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#1779BA',
        hoverBorderColor: '#1779BA',
      },
      Card: {
        headerBg: '#F2F2F2',
        headerHeight: 48,
        paddingLG: 20,
        borderRadius: 0,
        boxShadow: 'none',
      },
      Table: {
        headerBg: '#F2F2F2',
        headerColor: '#0A0A0A',
        rowHoverBg: '#E6E6E6',
        borderColor: '#CACACA',
        fontSize: 16,
      },
      Select: {
        controlHeight: 39,
        borderRadius: 0,
        fontSize: 16,
        colorBorder: '#CACACA',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#1779BA',
        hoverBorderColor: '#1779BA',
      },
      DatePicker: {
        controlHeight: 39,
        borderRadius: 0,
        fontSize: 16,
        colorBorder: '#CACACA',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#1779BA',
        hoverBorderColor: '#1779BA',
      },
      Switch: {
        colorPrimary: '#1779BA',
        colorPrimaryHover: '#1468A0',
        colorTextTertiary: '#CACACA',
      },
    },
  },
  bulma: {
    token: {
      colorPrimary: '#00D1B2', // Bulma primary turquoise
      borderRadius: 4,
      fontSize: 16,
      fontFamily: `BlinkMacSystemFont, -apple-system, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", Helvetica, Arial, sans-serif`,
      colorSuccess: '#48C774', // Bulma success green
      colorWarning: '#FFDD57', // Bulma warning yellow
      colorError: '#F14668', // Bulma danger red
      colorInfo: '#3273DC', // Bulma info blue
      colorLink: '#3273DC',
      colorLinkHover: '#276CDA',
      colorLinkActive: '#1E64D5',
      colorTextBase: '#4A4A4A', // Bulma default text
      colorBgBase: '#FFFFFF',
    },
    components: {
      Button: {
        colorPrimary: '#00D1B2',
       // colorText: '#FFFFFF',
        controlHeight: 40,
        borderRadius: 4,
        fontSize: 16,
        fontWeight: 400,
        boxShadow: 'none',
        algorithm: true,
      },
      Input: {
        controlHeight: 40,
        borderRadius: 4,
        fontSize: 16,
        colorBorder: '#DBDBDB',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#3273DC',
        hoverBorderColor: '#B5B5B5',
      },
      Card: {
        headerBg: '#FFFFFF',
        headerHeight: 48,
        paddingLG: 20,
        borderRadius: 4,
        boxShadow: '0 2px 3px rgba(10, 10, 10, 0.1), 0 0 0 1px rgba(10, 10, 10, 0.1)',
      },
      Table: {
        headerBg: '#FFFFFF',
        headerColor: '#363636',
        rowHoverBg: '#F5F5F5',
        borderColor: '#DBDBDB',
        fontSize: 16,
      },
      Select: {
        controlHeight: 40,
        borderRadius: 4,
        fontSize: 16,
        colorBorder: '#DBDBDB',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#3273DC',
        hoverBorderColor: '#B5B5B5',
      },
      DatePicker: {
        controlHeight: 40,
        borderRadius: 4,
        fontSize: 16,
        colorBorder: '#DBDBDB',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#3273DC',
        hoverBorderColor: '#B5B5B5',
      },
      Switch: {
        colorPrimary: '#00D1B2',
        colorPrimaryHover: '#00C4A7',
        colorTextTertiary: '#DBDBDB',
      },
    },
  },
  fluentUi: {
    token: {
      colorPrimary: '#0078D4', // Fluent UI primary blue
      borderRadius: 2,
      fontSize: 14,
      fontFamily: `'Segoe UI', 'Segoe UI Web (West European)', 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif`,
      colorSuccess: '#107C10', // Fluent UI success green
      colorWarning: '#D83B01', // Fluent UI warning orange
      colorError: '#A80000', // Fluent UI error red
      colorInfo: '#0078D4',
      colorLink: '#0078D4',
      colorLinkHover: '#005A9E',
      colorLinkActive: '#004578',
      colorTextBase: '#323130', // Fluent UI neutral primary
      colorBgBase: '#FFFFFF',
    },
    components: {
      Button: {
        colorPrimary: '#0078D4',
      //  colorText: '#FFFFFF',
        controlHeight: 32,
        borderRadius: 2,
        fontSize: 14,
        fontWeight: 600,
        boxShadow: 'none',
        algorithm: true,
      },
      Input: {
        controlHeight: 32,
        borderRadius: 2,
        fontSize: 14,
        colorBorder: '#8A8886', // Fluent UI neutral secondary
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#0078D4',
        hoverBorderColor: '#323130',
      },
      Card: {
        headerBg: '#FFFFFF',
        headerHeight: 48,
        paddingLG: 16,
        borderRadius: 2,
        boxShadow: '0 1.2px 3.6px rgba(0,0,0,0.18), 0 6.4px 14.4px rgba(0,0,0,0.22)',
      },
      Table: {
        headerBg: '#FFFFFF',
        headerColor: '#323130',
        rowHoverBg: '#F3F2F1', // Fluent UI neutral lighter
        borderColor: '#EDEBE9', // Fluent UI neutral light
        fontSize: 14,
      },
      Select: {
        controlHeight: 32,
        borderRadius: 2,
        fontSize: 14,
        colorBorder: '#8A8886',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#0078D4',
        hoverBorderColor: '#323130',
      },
      DatePicker: {
        controlHeight: 32,
        borderRadius: 2,
        fontSize: 14,
        colorBorder: '#8A8886',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#0078D4',
        hoverBorderColor: '#323130',
      },
      Switch: {
        colorPrimary: '#0078D4',
        colorPrimaryHover: '#005A9E',
        colorTextTertiary: '#C8C6C4', // Fluent UI neutral tertiary
      },
    },
  },
  nextUi: {
    token: {
      colorPrimary: '#0072F5', // NextUI primary blue
      borderRadius: 12,
      fontSize: 16,
      fontFamily: `'Inter', sans-serif`,
      colorSuccess: '#17C964', // NextUI success green
      colorWarning: '#F5A524', // NextUI warning yellow
      colorError: '#F31260', // NextUI error red
      colorInfo: '#0072F5',
      colorLink: '#0072F5',
      colorLinkHover: '#0062D1',
      colorLinkActive: '#0052AD',
      colorTextBase: '#11181C', // NextUI text
      colorBgBase: '#FFFFFF',
    },
    components: {
      Button: {
        colorPrimary: '#0072F5',
       // colorText: '#FFFFFF',
        controlHeight: 40,
        borderRadius: 12,
        fontSize: 16,
        fontWeight: 600,
        boxShadow: '0 4px 14px 0 rgba(0,118,255,0.39)',
        algorithm: true,
      },
      Input: {
        controlHeight: 40,
        borderRadius: 12,
        fontSize: 16,
        colorBorder: '#EAEAEA',
        colorBgContainer: '#F1F3F5',
        activeBorderColor: '#0072F5',
        hoverBorderColor: '#0072F5',
      },
      Card: {
        headerBg: '#FFFFFF',
        headerHeight: 48,
        paddingLG: 24,
        borderRadius: 12,
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
      },
      Table: {
        headerBg: '#F9FAFB',
        headerColor: '#697177',
        rowHoverBg: '#F1F3F5',
        borderColor: '#EAEAEA',
        fontSize: 14,
      },
      Select: {
        controlHeight: 40,
        borderRadius: 12,
        fontSize: 16,
        colorBorder: '#EAEAEA',
        colorBgContainer: '#F1F3F5',
        activeBorderColor: '#0072F5',
        hoverBorderColor: '#0072F5',
      },
      DatePicker: {
        controlHeight: 40,
        borderRadius: 12,
        fontSize: 16,
        colorBorder: '#EAEAEA',
        colorBgContainer: '#F1F3F5',
        activeBorderColor: '#0072F5',
        hoverBorderColor: '#0072F5',
      },
      Switch: {
        colorPrimary: '#0072F5',
        colorPrimaryHover: '#0062D1',
        colorTextTertiary: '#EAEAEA',
      },
    },
  },
  mantine: {
    token: {
      colorPrimary: '#228BE6', // Mantine primary blue
      borderRadius: 4,
      fontSize: 16,
      fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji'`,
      colorSuccess: '#40C057', // Mantine success green
      colorWarning: '#FAB005', // Mantine warning yellow
      colorError: '#FA5252', // Mantine error red
      colorInfo: '#228BE6',
      colorLink: '#228BE6',
      colorLinkHover: '#1C7ED6',
      colorLinkActive: '#1971C2',
      colorTextBase: '#212529', // Mantine default text
      colorBgBase: '#FFFFFF',
    },
    components: {
      Button: {
        colorPrimary: '#228BE6',
       // colorText: '#FFFFFF',
        controlHeight: 36,
        borderRadius: 4,
        fontSize: 16,
        fontWeight: 600,
        boxShadow: 'none',
        algorithm: true,
      },
      Input: {
        controlHeight: 36,
        borderRadius: 4,
        fontSize: 16,
        colorBorder: '#CED4DA',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#228BE6',
        hoverBorderColor: '#ADB5BD',
      },
      Card: {
        headerBg: '#FFFFFF',
        headerHeight: 48,
        paddingLG: 16,
        borderRadius: 4,
        boxShadow: 'none',
      },
      Table: {
        headerBg: '#FFFFFF',
        headerColor: '#495057',
        rowHoverBg: '#F8F9FA',
        borderColor: '#DEE2E6',
        fontSize: 16,
      },
      Select: {
        controlHeight: 36,
        borderRadius: 4,
        fontSize: 16,
        colorBorder: '#CED4DA',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#228BE6',
        hoverBorderColor: '#ADB5BD',
      },
      DatePicker: {
        controlHeight: 36,
        borderRadius: 4,
        fontSize: 16,
        colorBorder: '#CED4DA',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#228BE6',
        hoverBorderColor: '#ADB5BD',
      },
      Switch: {
        colorPrimary: '#228BE6',
        colorPrimaryHover: '#1C7ED6',
        colorTextTertiary: '#CED4DA',
      },
    },
  },
  antDesign: {
    token: {
      colorPrimary: '#1890ff',
      borderRadius: 2,
      fontSize: 14,
      fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'`,
      colorSuccess: '#52c41a',
      colorWarning: '#faad14',
      colorError: '#ff4d4f',
      colorInfo: '#1890ff',
      colorLink: '#1890ff',
      colorLinkHover: '#40a9ff',
      colorLinkActive: '#096dd9',
      colorTextBase: 'rgba(0, 0, 0, 0.85)',
      colorBgBase: '#ffffff',
    },
  },
  primeReact: {
    token: {
      colorPrimary: '#2196F3', // PrimeReact primary blue
      borderRadius: 4,
      fontSize: 14,
      fontFamily: `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`,
      colorSuccess: '#4CAF50', // PrimeReact success green
      colorWarning: '#FFC107', // PrimeReact warning yellow
      colorError: '#F44336', // PrimeReact error red
      colorInfo: '#2196F3',
      colorLink: '#2196F3',
      colorLinkHover: '#1E88E5',
      colorLinkActive: '#1976D2',
      colorTextBase: '#495057', // PrimeReact text color
      colorBgBase: '#F8F9FA',
    },
    components: {
      Button: {
        colorPrimary: '#2196F3',
      //  colorText: '#FFFFFF',
        controlHeight: 36,
        borderRadius: 4,
        fontSize: 14,
        fontWeight: 700,
        boxShadow: '0 2px 3px 0 rgba(0,0,0,0.15)',
        algorithm: true,
      },
      Input: {
        controlHeight: 36,
        borderRadius: 4,
        fontSize: 14,
        colorBorder: '#CED4DA',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#2196F3',
        hoverBorderColor: '#2196F3',
      },
      Card: {
        headerBg: '#F8F9FA',
        headerHeight: 56,
        paddingLG: 24,
        borderRadius: 4,
        boxShadow: '0 2px 1px -1px rgba(0,0,0,0.2), 0 1px 1px 0 rgba(0,0,0,0.14), 0 1px 3px 0 rgba(0,0,0,0.12)',
      },
      Table: {
        headerBg: '#F8F9FA',
        headerColor: '#495057',
        rowHoverBg: '#E9ECEF',
        borderColor: '#DEE2E6',
        fontSize: 14,
      },
      Select: {
        controlHeight: 36,
        borderRadius: 4,
        fontSize: 14,
        colorBorder: '#CED4DA',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#2196F3',
        hoverBorderColor: '#2196F3',
      },
      DatePicker: {
        controlHeight: 36,
        borderRadius: 4,
        fontSize: 14,
        colorBorder: '#CED4DA',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#2196F3',
        hoverBorderColor: '#2196F3',
      },
      Switch: {
        colorPrimary: '#2196F3',
        colorPrimaryHover: '#1E88E5',
        colorTextTertiary: '#CED4DA',
      },
    },
  },
  rebass: {
    token: {
      colorPrimary: '#07c', // Rebass primary blue
      borderRadius: 4,
      fontSize: 16,
      fontFamily: `system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif`,
      colorSuccess: '#0a0',
      colorWarning: '#f90',
      colorError: '#c00',
      colorInfo: '#07c',
      colorLink: '#07c',
      colorLinkHover: '#06b',
      colorLinkActive: '#05a',
      colorTextBase: '#000',
      colorBgBase: '#fff',
    },
    components: {
      Button: {
        colorPrimary: '#07c',
       // colorText: '#fff',
        controlHeight: 40,
        borderRadius: 4,
        fontSize: 16,
        fontWeight: 700,
        boxShadow: 'none',
        algorithm: true,
      },
      Input: {
        controlHeight: 40,
        borderRadius: 4,
        fontSize: 16,
        colorBorder: '#ddd',
        colorBgContainer: '#fff',
        activeBorderColor: '#07c',
        hoverBorderColor: '#07c',
      },
      Card: {
        headerBg: '#f6f6f6',
        headerHeight: 48,
        paddingLG: 16,
        borderRadius: 4,
        boxShadow: '0 2px 4px rgba(0,0,0,0.125)',
      },
      Table: {
        headerBg: '#f6f6f6',
        headerColor: '#000',
        rowHoverBg: '#f6f6f6',
        borderColor: '#ddd',
        fontSize: 16,
      },
      Select: {
        controlHeight: 40,
        borderRadius: 4,
        fontSize: 16,
        colorBorder: '#ddd',
        colorBgContainer: '#fff',
        activeBorderColor: '#07c',
        hoverBorderColor: '#07c',
      },
      DatePicker: {
        controlHeight: 40,
        borderRadius: 4,
        fontSize: 16,
        colorBorder: '#ddd',
        colorBgContainer: '#fff',
        activeBorderColor: '#07c',
        hoverBorderColor: '#07c',
      },
      Switch: {
        colorPrimary: '#07c',
        colorPrimaryHover: '#06b',
        colorTextTertiary: '#ddd',
      },
    },
  },
  grommet: {
    token: {
      colorPrimary: '#7D4CDB', // Grommet brand color
      borderRadius: 4,
      fontSize: 18,
      fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif`,
      colorSuccess: '#00C781',
      colorWarning: '#FFCA58',
      colorError: '#FF4040',
      colorInfo: '#7D4CDB',
      colorLink: '#7D4CDB',
      colorLinkHover: '#6C37B9',
      colorLinkActive: '#5B2497',
      colorTextBase: '#444444',
      colorBgBase: '#FFFFFF',
    },
    components: {
      Button: {
        colorPrimary: '#7D4CDB',
       // colorText: '#FFFFFF',
        controlHeight: 48,
        borderRadius: 4,
        fontSize: 18,
        fontWeight: 700,
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
        algorithm: true,
      },
      Input: {
        controlHeight: 48,
        borderRadius: 4,
        fontSize: 18,
        colorBorder: 'rgba(0, 0, 0, 0.33)',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#7D4CDB',
        hoverBorderColor: '#7D4CDB',
      },
      Card: {
        headerBg: '#FFFFFF',
        headerHeight: 64,
        paddingLG: 24,
        borderRadius: 4,
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
      },
      Table: {
        headerBg: '#FFFFFF',
        headerColor: '#444444',
        rowHoverBg: 'rgba(221, 221, 221, 0.4)',
        borderColor: 'rgba(0, 0, 0, 0.33)',
        fontSize: 18,
      },
      Select: {
        controlHeight: 48,
        borderRadius: 4,
        fontSize: 18,
        colorBorder: 'rgba(0, 0, 0, 0.33)',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#7D4CDB',
        hoverBorderColor: '#7D4CDB',
      },
      DatePicker: {
        controlHeight: 48,
        borderRadius: 4,
        fontSize: 18,
        colorBorder: 'rgba(0, 0, 0, 0.33)',
        colorBgContainer: '#FFFFFF',
        activeBorderColor: '#7D4CDB',
        hoverBorderColor: '#7D4CDB',
      },
      Switch: {
        colorPrimary: '#7D4CDB',
        colorPrimaryHover: '#6C37B9',
        colorTextTertiary: 'rgba(0, 0, 0, 0.33)',
      },
    },
  },
};